import { UserToken } from "@app/store/useUserVarStore";
import fetchBHApi from "@helper/fetchBHApi";
import { shouldShowReactions } from "@helper/shouldShowReactions";
import { useGuardedFetchRequest } from "@helper/useGuardedFetchRequest";
import {
  CD_LANGUAGE,
  useBHCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { useFetchRequest } from "@hooks/useFetchRequest";
import {
  HandleReactionClick,
  ReactionProp,
  ReactionsApiResponse,
  ReactionsDataRequest,
  ReactionsDataResponse,
  UserReactionsResponse,
} from "@typings/Reactions.d";
import { useCallback, useEffect, useState } from "react";

type UseArticleReactionProps = {
  articleId: string;
  userToken: UserToken;
  publishDate: string;
  uniqueSectionName?: string;
};

export function useArticleReactions({
  articleId,
  userToken,
  publishDate,
  uniqueSectionName,
}: UseArticleReactionProps) {
  const [reactions, setReactions] = useState<ReactionProp[]>([]);
  const [reactionSetId, setReactionSetId] = useState<string>("");
  const [userReactions, setUserReactions] = useState<string>("0");

  const isValidArticleId = typeof articleId === "string";
  const showReactions = shouldShowReactions(publishDate, uniqueSectionName);
  const bhCustomEvent = useBHCustomEventAnalyticsCall();

  // Fetch article reactions
  const { data: reactionsResults, error: reactionsError } =
    useFetchRequest<ReactionsDataResponse>({
      endpoint: "reactions/get-reaction",
      payload: { articleId, userToken },
      skip: !showReactions || !isValidArticleId,
    });

  useEffect(() => {
    if (!reactionsError) {
      setReactions(reactionsResults?.reactions || []);
      setReactionSetId(reactionsResults?.set || "");
    }
  }, [reactionsResults, reactionsError]);

  // Fetch user’s existing reaction
  const { data: reactionsUserResults, error: reactionsUserError } =
    useGuardedFetchRequest<UserReactionsResponse>(
      !!userToken.id && showReactions,
      {
        endpoint: "reactions/get-user-reaction",
        payload: { articleId, userToken },
      },
    );

  useEffect(() => {
    if (!reactionsUserError) {
      setUserReactions(reactionsUserResults?.react || "0");
    }
  }, [reactionsUserResults, reactionsUserError]);

  // Handle reaction click
  const handleReactionClick: HandleReactionClick = useCallback(
    async (reactId, variant) => {
      const prevReactId = userReactions;

      if (prevReactId !== "0" && reactId === prevReactId) {
        reactId = "0";
      }

      setReactions((prevReactions) =>
        prevReactions.map((reaction) => {
          if (reaction.id === reactId) {
            return { ...reaction, count: reaction.count + 1 };
          } else if (reaction.id === prevReactId) {
            return { ...reaction, count: reaction.count - 1 };
          }
          return reaction;
        }),
      );
      setUserReactions(reactId);

      try {
        if (!showReactions) {
          throw new Error("Reaction is disabled");
        }

        const url = `reactions/${reactId !== "0" ? "user-reacts" : "delete-user-reacts"}`;
        const requestData: ReactionsDataRequest = {
          articleId,
          userToken,
          reactId,
          setId: reactionSetId,
        };

        const results = await fetchBHApi<
          ReactionsApiResponse<ReactionsDataResponse>
        >(url, "POST", requestData);

        if (results.success) {
          bhCustomEvent(
            "reaction",
            `${variant}::${reactId}`,
            window.location.href,
            true,
            undefined,
            CD_LANGUAGE.MALAY,
            undefined,
          );
        } else {
          // rollback
          setReactions((prevReactions) =>
            prevReactions.map((reaction) => {
              if (reaction.id === reactId) {
                return { ...reaction, count: reaction.count - 1 };
              } else if (reaction.id === prevReactId) {
                return { ...reaction, count: reaction.count + 1 };
              }
              return reaction;
            }),
          );
        }
      } catch (err) {
        console.error("Error posting reaction:", err);
      }
    },
    [
      articleId,
      userToken,
      reactionSetId,
      userReactions,
      showReactions,
      bhCustomEvent,
    ],
  );

  return {
    reactions,
    reactionSetId,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  };
}
